import React, { useState, useEffect } from 'react';
import HeroBanner from '../components/HeroBanner';
import Footer from '../components/Footer';
import './Curriculum.css';

const Curriculum = () => {
    const [curriculums, setCurriculums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedGrade, setSelectedGrade] = useState('All');

    useEffect(() => {
        fetchCurriculums();
    }, []);

    const fetchCurriculums = async () => {
        try {
            setLoading(true);
            const response = await fetch('/admin/api/curriculum.php');
            const result = await response.json();
            
            if (result.success) {
                setCurriculums(result.data || []);
            } else {
                setError('Failed to load curriculum data');
            }
        } catch (err) {
            setError('Error loading curriculum data');
            console.error('Error fetching curriculums:', err);
        } finally {
            setLoading(false);
        }
    };

    const getCategoryColor = (category) => {
        switch(category) {
            case 'Early Years': return 'bg-yellow-100 text-yellow-800';
            case 'Primary': return 'bg-blue-100 text-blue-800';
            case 'Middle': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getUniqueCategories = () => {
        const categories = [...new Set(curriculums.map(c => c.category))];
        return ['All', ...categories];
    };

    const getUniqueGrades = () => {
        const grades = [...new Set(curriculums.map(c => c.grade))];
        return ['All', ...grades];
    };

    const filteredCurriculums = curriculums.filter(curriculum => {
        const categoryMatch = selectedCategory === 'All' || curriculum.category === selectedCategory;
        const gradeMatch = selectedGrade === 'All' || curriculum.grade === selectedGrade;
        return categoryMatch && gradeMatch;
    });

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-4">Loading curriculum...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <i className="fas fa-exclamation-triangle text-6xl text-red-500 mb-4"></i>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Curriculum</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button 
                        onClick={fetchCurriculums}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Banner Section */}
            <div className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0 -z-20">
                    <img
                        src="assets/hero-banner.jpg"
                        alt="PAK School & College Curriculum"
                        className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-black/90"></div>
                </div>
                
                {/* Content */}
                <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-8 sm:p-12 border border-white/10">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
                            Academic <span className="text-blue-400 drop-shadow-lg">Curriculum</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-white max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
                            Explore our comprehensive curriculum designed to provide quality education 
                            across all grade levels and subjects.
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Category</label>
                            <select 
                                value={selectedCategory} 
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {getUniqueCategories().map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Grade</label>
                            <select 
                                value={selectedGrade} 
                                onChange={(e) => setSelectedGrade(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {getUniqueGrades().map(grade => (
                                    <option key={grade} value={grade}>{grade}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results Count */}
                <div className="mb-4">
                    <p className="text-sm text-gray-600">
                        Showing {filteredCurriculums.length} curriculum{filteredCurriculums.length !== 1 ? 's' : ''}
                        {selectedCategory !== 'All' && ` in ${selectedCategory}`}
                        {selectedGrade !== 'All' && ` for ${selectedGrade}`}
                    </p>
                </div>

                {/* Curriculum Grid */}
                {filteredCurriculums.length === 0 ? (
                    <div className="text-center py-12">
                        <i className="fas fa-graduation-cap text-6xl text-gray-400 mb-4"></i>
                        <h3 className="text-gray-600 mb-2">No curriculum found</h3>
                        <p className="text-gray-500">Try adjusting your filters to see more results.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredCurriculums.map(curriculum => (
                            <div key={curriculum.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                        <i className="fas fa-graduation-cap text-green-600"></i>
                                    </div>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(curriculum.category)}`}>
                                        {curriculum.category}
                                    </span>
                                </div>
                                
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">{curriculum.grade}</h3>
                                <h4 className="text-sm font-medium text-gray-700 mb-1">{curriculum.subject}</h4>
                                <h5 className="text-sm font-medium text-gray-600 mb-2">{curriculum.title}</h5>
                                <p className="text-xs text-gray-500 mb-2">Academic Year: {curriculum.academic_year}</p>
                                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                                    {curriculum.description || 'No description available'}
                                </p>
                                
                                <div className="flex items-center justify-between">
                                    <div className="text-xs text-gray-500">
                                        Updated: {new Date(curriculum.updated_at).toLocaleDateString()}
                                    </div>
                                    {curriculum.file && (
                                        <a 
                                            href={`/admin/assets/curriculum/${curriculum.file}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors"
                                        >
                                            <i className="fas fa-download mr-1"></i>
                                            Download
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Info Section */}
            <div className="bg-blue-50 border-t border-blue-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">About Our Curriculum</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-book-open text-2xl text-blue-600"></i>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Comprehensive</h3>
                                <p className="text-gray-600">Our curriculum covers all essential subjects and learning objectives for each grade level.</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-users text-2xl text-green-600"></i>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Student-Centered</h3>
                                <p className="text-gray-600">Designed to meet the diverse learning needs and developmental stages of our students.</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-chart-line text-2xl text-yellow-600"></i>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Progressive</h3>
                                <p className="text-gray-600">Structured to build upon previous knowledge and prepare students for future learning.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Curriculum;
